// External Libraries
import * as React from "react";
import { Viewport, ViewSmall, ViewMedium } from "bernie-view";
import { observer } from "mobx-react";
import { useLocalization } from "@shared-ui/localization-context";
import { useBexApi } from "@shared-ui/bex-api-context";

// Components
import { HotelsContext } from "components/flexComponents/Hotels/components/HotelsContext";
import { SummarizedHotelCard } from "components/flexComponents/Hotels/components/SummarizedHotelCard/SummarizedHotelCard";
import IsomorphicCarousel from "components/shared/IsomorphicCarousel/IsomorphicCarousel";
import { SummarizedHotelsCommonView } from "components/flexComponents/Hotels/components/SummarizedHotelCard/SummarizedHotelsCommonView";
import { useMapPropertySearchByIdQuery } from "components/flexComponents/Hotels/hooks/useMapPropertySearchByIdQuery";
import { withStores } from "src/stores";
import { hotelStructuredData } from "components/flexComponents/Hotels/utils/getHotelStructuredData";
import { SchemaOrgScript } from "components/utility/SchemaOrg/SchemaOrgScript";
import { LazyLoad } from "src/components/utility/LazyLoad/LazyLoad";

// Types
import { HotelsProps, SummarizedHotelsQueryComponentProps } from "components/flexComponents/Hotels/typings";
import { HotelsFlexModuleResult } from "typings/microserviceModels/hotels-flex-module";

// Uitk
import { UitkSpacing } from "uitk-react-spacing";
import { getFmId } from "src/stores/ExperienceTemplateStore";

const SummarizedHotelsCarousel: React.FC<HotelsProps> = (props: HotelsProps) => {
  const { templateComponent, context, hotels, flexModuleModelStore } = props;

  if (!templateComponent) {
    return null;
  }

  const {
    metadata: { id },
  } = templateComponent;
  const model = flexModuleModelStore.getModel(id) as HotelsFlexModuleResult | null;

  if (!model || model.hotels?.length === 0) {
    return null;
  }

  return (
    <SummarizedHotelsCarouselQueryComponent
      templateComponent={templateComponent}
      context={context}
      model={model}
      hotels={hotels}
    />
  );
};

const SummarizedHotelsCarouselQueryComponent = (props: SummarizedHotelsQueryComponentProps) => {
  const { templateComponent, context, model, hotels: hotelsStore } = props;

  const {
    metadata: { id },
    config: { fmTitleId },
  } = templateComponent;
  const fmId = getFmId(templateComponent);
  const { context: bexApiContext } = useBexApi();
  const { formatText } = useLocalization();
  const [hasSetHotelsOnStore, setHasSetHotelsOnStore] = React.useState(false);
  const showHotelsOnMap = typeof model.showMarkersOnMap === "undefined" || model.showMarkersOnMap === true;
  const shouldSkipQuery = (!(model.hotels.length > 0) && !showHotelsOnMap) || hasSetHotelsOnStore;

  const { data, error } = useMapPropertySearchByIdQuery(
    model.hotels.map((hotel) => hotel.hotelId),
    hotelsStore.getSearchDateRange(),
    bexApiContext,
    shouldSkipQuery
  );

  if (data) {
    hotelsStore.setAllHotelsOnMapOnSuccessResponse(data.propertySearch.properties, model.destinationId);

    setHasSetHotelsOnStore(true);
  }

  if (error) {
    hotelsStore.setAllHotelsOnMapOnFailureResponse(model.hotels, context, model.destinationId);
  }

  return (
    <LazyLoad context={context}>
      <HotelsContext.Provider value={{ context, templateComponent, model }}>
        <SchemaOrgScript structuredData={hotelStructuredData(model.hotels)} />
        <UitkSpacing padding={{ block: "three" }}>
          <div className="SummarizedHotelsCarousel Hotels" id={id} data-fm={fmId} data-fm-title-id={fmTitleId}>
            <SummarizedHotelsCommonView>
              <UitkSpacing padding={{ blockstart: "three" }}>
                <IsomorphicCarousel
                  itemsVisible={{ sm: 1, md: 2, lg: 2 }}
                  pageBy={1}
                  peekWidth={12}
                  buttonText={{
                    nextButton: formatText("carousel.item.next"),
                    prevButton: formatText("carousel.item.prev"),
                  }}
                >
                  {model.hotels.map((hotel, index) => (
                    <Viewport key={`summarized-hotels-carousel-${hotel.hotelId}`}>
                      <ViewSmall>
                        <SummarizedHotelCard key={hotel.hotelId} hotel={hotel} index={index} isMobile />
                      </ViewSmall>
                      <ViewMedium>
                        <SummarizedHotelCard key={hotel.hotelId} hotel={hotel} index={index} isMobile={false} />
                      </ViewMedium>
                    </Viewport>
                  ))}
                </IsomorphicCarousel>
              </UitkSpacing>
            </SummarizedHotelsCommonView>
          </div>
        </UitkSpacing>
      </HotelsContext.Provider>
    </LazyLoad>
  );
};

export const SummarizedHotelsCarouselView = withStores(
  "hotels",
  "context",
  "flexModuleModelStore"
)(observer(SummarizedHotelsCarousel));

export default SummarizedHotelsCarouselView;
